import ProductVideo from '@common/components/ProductVideo';
import { PRODUCT_TYPE } from '@common/models/Product';
import { getYoutubeVideoThumbnail, isMobile, matchYoutubeUrlRegex } from '@common/utils';
import { getLambdaLink } from '@common/utils';
import { ReactComponent as ArrowDown } from '@images/ArrowDown.svg';
import { ReactComponent as ArrowLeft } from '@images/ArrowLeft.svg';
import { ReactComponent as ArrowRight } from '@images/ArrowRight.svg';
import { ReactComponent as Close } from '@images/Close.svg';
import { ReactComponent as ShipIcon } from '@images/Shipping.svg';
import { PlayButtonIcon } from '@instamojo/icons';
import AspectRatio from '@lib/AspectRatio';
import Image from '@lib/Image';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import Modal from './Modal';

export interface GalleryProps {
  className?: string;
  isZoomEnabled: boolean;
  images: string[];
  quickView?: boolean;
  imageDescription?: string;
  productVideoUrl?: string;
  productVideoEnabled?: boolean;
}

const Gallery: React.FunctionComponent<GalleryProps> = ({
  className = '',
  images = [],
  quickView,
  imageDescription = '',
  productVideoUrl,
  productVideoEnabled,
  product,
  finalProduct,
}) => {
  const [viewAll, setViewAll] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [imagesGallery, setImagesGallery];

  const imagesGallery = [...images];
  if (productVideoEnabled) {
    imagesGallery.push(productVideoUrl);
  }

  const imageRef = useRef(null);
  const divRef = useRef(null);
  const selectedImageRef = useRef(null);
  const [height, setHeight] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (ref) {
      setHeight(ref?.current?.clientHeight);
    }
  });

  useEffect(() => {
    if (selectedImageRef && isModalOpen) {
      selectedImageRef?.current?.scrollIntoView({
        behavior: 'smooth',
        // block: 'end',
        // inline: 'end',
      });
    }
  }, [isModalOpen, selectedImageIndex]);

  return (
    <div className={className} ref={divRef}>
      {!quickView || (quickView && isMobile()) ? (
        <div className="relative gallery-carousel">
          <div
            className={classnames('flex flex-no-wrap gap-24 md:flex-wrap overflow-x-auto pb-16 md:pb-0 w-full', {
              'custom-scrollbar': isMobile(),
            })}
          >
            {imagesGallery.slice(0, viewAll || isMobile() ? imagesGallery.length : 3).map((image, index) => {
              return (
                <div
                  key={image}
                  className="min-w-full md:min-w-0 flex-2col md:first:flex-100p flex justify-center items-center cursor-zoomIn"
                  onClick={() => {
                    if (!isMobile()) {
                      setIsModalOpen(true);
                      setSelectedImageIndex(index);
                    }
                  }}
                >
                  <AspectRatio aspectRatio={185 / 278}>
                    {matchYoutubeUrlRegex(image) && productVideoEnabled ? (
                      isMobile() ? (
                        <ProductVideo
                          className="h-full w-full"
                          productVideoUrl={productVideoUrl}
                          height={'100%'}
                          width={'100%'}
                        />
                      ) : (
                        <>
                          <div className="absolute w-full h-full zIndex-999 flex items-center justify-center">
                            <PlayButtonIcon size="24" color="#000" />
                          </div>
                          <Image
                            src={getYoutubeVideoThumbnail(productVideoUrl)}
                            alt={imageDescription}
                            className={classnames('object-contain h-full w-full', {})}
                            noDefaultDimensions
                            noDefaultFit
                          />
                        </>
                      )
                    ) : (
                      <figure className="w-full h-full">
                        <img
                          ref={imageRef}
                          className={classnames('object-cover w-full h-full')}
                          loading={'lazy'}
                          src={getLambdaLink({
                            link: image,
                            width: 0,
                            height: 0,
                          })}
                          alt={imageDescription}
                        />
                      </figure>
                    )}
                  </AspectRatio>
                </div>
              );
            })}
            <div className="hidden md:block flex-2col min-w-full md:min-w-0"></div>
            <div className="hidden md:block flex-2col min-w-full md:min-w-0"></div>
          </div>
          {imagesGallery.length > 3 && !isMobile() ? (
            <button
              className="hidden md:flex justify-center items-center shadow-showMore relative bg-white galley-view-btn mx-auto -mt-42 z-30 p-8 w-150 text-center text-heading border border-heading"
              onClick={() => {
                setViewAll(!viewAll);
                if (viewAll)
                  setTimeout(() => {
                    document.getElementsByClassName(`galley-view-btn`)[0].scrollIntoView({
                      behavior: 'smooth',
                      block: 'end',
                    });
                  });
              }}
            >
              <p>{viewAll ? 'Show Less' : 'Show More'}</p>
              <ArrowDown className={viewAll ? 'transform rotate-180' : ''} />
            </button>
          ) : null}
          {product.type === PRODUCT_TYPE.PHYSICAL ? (
            <div className="md:hidden absolute bottom-30 left-30 flex justify-center items-center rounded-full px-12 py-6 bg-black text-13 text-white">
              <ShipIcon className="h-20 w-20 tex-white"></ShipIcon>
              <div className="ml-6" font-customisation="para-text" color-customisation="page-body-text">
                {finalProduct.shipInDays} Day Shipping
              </div>
            </div>
          ) : null}
          {finalProduct?.hasDiscount ? (
            <div className="absolute top-16 left-16 rounded-full text-13 px-12 py-6 bg-black text-white">
              On Discount
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex w-full">
            <div className="flex min-w-60 h-489 flex-col flex-no-wrap overflow-x-auto mr-8">
              {imagesGallery.map((image, index) => {
                return (
                  <div
                    className={`relative mb-8 w-60 h-90 min-h-90 max-h-90 ${
                      index === selectedImageIndex ? 'border-2 border-medium' : ''
                    }`}
                    key={index}
                    onMouseEnter={() => setSelectedImageIndex(index)}
                    id={`image-${index}`}
                    ref={selectedImageRef}
                  >
                    <div
                      className={`absolute w-full h-full zIndex-999 ${
                        index === selectedImageIndex ? ' bg-gray opacity-30' : 'bg-white opacity-30'
                      }`}
                    ></div>
                    {matchYoutubeUrlRegex(image) ? (
                      <div className="relative h-full">
                        <div className="absolute w-full h-full zIndex-999 bg-gray opacity-80 flex items-center justify-center">
                          <PlayButtonIcon size="24" color="#000" />
                        </div>
                        <Image
                          src={getYoutubeVideoThumbnail(productVideoUrl)}
                          alt={imageDescription}
                          className={classnames('object-cover max-w-none w-full h-full', {})}
                          noDefaultDimensions
                          noDefaultFit
                        />
                      </div>
                    ) : (
                      <Image
                        src={image}
                        alt={imageDescription}
                        className={classnames('object-cover w-full h-full', {})}
                        key={image}
                        noDefaultDimensions
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="relative w-326 flex-shrink-0 h-489">
              {matchYoutubeUrlRegex(imagesGallery[selectedImageIndex]) && productVideoEnabled ? (
                <ProductVideo
                  className="h-full w-full"
                  productVideoUrl={imagesGallery[selectedImageIndex]}
                  autoPlay={true}
                  height={'100%'}
                  width={'100%'}
                />
              ) : (
                <figure className="w-full h-full">
                  <img
                    ref={imageRef}
                    className={classnames('object-cover w-full h-full')}
                    loading={'lazy'}
                    src={getLambdaLink({
                      link: imagesGallery[selectedImageIndex],
                      width: 0,
                      height: 0,
                    })}
                    alt={imageDescription}
                  />
                </figure>
              )}
              {product.type === PRODUCT_TYPE.PHYSICAL ? (
                <div className="md:hidden absolute bottom-30 left-30 flex justify-center items-center rounded-full px-12 py-6 bg-black text-13 text-white">
                  <ShipIcon className="h-20 w-20 tex-white"></ShipIcon>
                  <div className="ml-6" font-customisation="para-text" color-customisation="page-body-text">
                    {finalProduct.shipInDays} Day Shipping
                  </div>
                </div>
              ) : null}
              {finalProduct?.hasDiscount ? (
                <div className="absolute top-16 left-16 rounded-full text-13 px-12 py-6 bg-black text-white">
                  On Discount
                </div>
              ) : null}
              {imagesGallery.length > 1 ? (
                <>
                  <ArrowLeft
                    className={classnames('absolute h-32 w-32 ml-12 top-0 bottom-0 my-auto left-0 cursor-pointer', {
                      'opacity-40': selectedImageIndex === 0,
                    })}
                    onClick={() => {
                      if (selectedImageIndex !== 0) {
                        setSelectedImageIndex(selectedImageIndex - 1);
                      }
                    }}
                  />
                  <ArrowRight
                    className={classnames('absolute h-32 w-32 top-0 mr-12 bottom-0 my-auto right-0 cursor-pointer', {
                      'opacity-40': selectedImageIndex === imagesGallery.length - 1,
                    })}
                    onClick={() => {
                      if (selectedImageIndex + 1 < imagesGallery.length) {
                        setSelectedImageIndex(selectedImageIndex + 1);
                      }
                    }}
                  />
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
      {isModalOpen ? (
        <Modal
          className="bg-white w-90p h-90p max-h-1200 max-w-1200 p-24"
          zIndex="z-999999"
          closeModal={() => setIsModalOpen(false)}
        >
          <div className="w-full flex justify-end mr-24">
            <Close className="cursor-pointer" onClick={() => setIsModalOpen(false)} />
          </div>
          <div className="relative h-full flex flex-col justify-between p-24">
            <div ref={ref} style={{ flex: '1' }} className="mb-24">
              <div className="relative w-full h-full">
                <div
                  className="absolute inset-0 mx-auto flex justify-center max-h-full items-center"
                  style={{ width: `${height * 0.6666}px` }}
                >
                  {matchYoutubeUrlRegex(imagesGallery[selectedImageIndex]) && productVideoEnabled ? (
                    <ProductVideo
                      className="h-full w-full"
                      productVideoUrl={imagesGallery[selectedImageIndex]}
                      autoPlay={true}
                      height={'100%'}
                      width={'100%'}
                    />
                  ) : (
                    <img
                      ref={imageRef}
                      className={classnames('object-contain margin-auto h-full w-full')}
                      loading={'lazy'}
                      src={getLambdaLink({
                        link: imagesGallery[selectedImageIndex],
                        width: 0,
                        height: 0,
                      })}
                      alt={imageDescription}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-no-wrap overflow-x-auto mx-auto max-w-600 ">
              {imagesGallery.map((image, index) => {
                return (
                  <div
                    className={`relative mr-8 w-82 min-w-82 h-122 min-h-122 max-h-1-22 flex justify-center cursor-pointer ${
                      index === selectedImageIndex ? 'border-2 border-black' : ''
                    }`}
                    key={index}
                    onMouseEnter={() => {
                      setSelectedImageIndex(index);
                    }}
                    id={`image-${index}`}
                    ref={index === selectedImageIndex ? selectedImageRef : null}
                  >
                    <div
                      className={`absolute inset-0 w-full h-full zIndex-999 ${
                        index === selectedImageIndex ? 'bg-black opacity-30' : 'bg-white opacity-30'
                      }`}
                    ></div>
                    {matchYoutubeUrlRegex(image) ? (
                      <div className="relative w-full h-full flex justify-center items-center">
                        <div className="absolute w-full h-full zIndex-999 bg-gray opacity-30 flex items-center justify-center">
                          <PlayButtonIcon size="24" color="#000" />
                        </div>
                        <Image
                          src={getYoutubeVideoThumbnail(productVideoUrl)}
                          alt={imageDescription}
                          className={classnames('object-contain', {})}
                          noDefaultDimensions
                          noDefaultFit
                        />
                      </div>
                    ) : (
                      <Image
                        src={getLambdaLink({
                          link: image,
                          width: 0,
                          height: 0,
                        })}
                        alt={imageDescription}
                        className={classnames('object-contain', {})}
                        key={image}
                        noDefaultDimensions
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <ArrowLeft
              className="absolute top-0 bottom-0 my-auto left-0 cursor-pointer"
              onClick={() => {
                if (selectedImageIndex !== 0) {
                  setSelectedImageIndex(selectedImageIndex - 1);
                }
              }}
            />
            <ArrowRight
              className="absolute top-0 bottom-0 my-auto right-0 cursor-pointer"
              onClick={() => {
                if (selectedImageIndex + 1 < imagesGallery.length) {
                  setSelectedImageIndex(selectedImageIndex + 1);
                }
              }}
            />
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

export default Gallery;
